import type { Action, IDocumentNode, PartialNode, Trigger } from "../node/type";
import type { IDataMap, PartialData } from "./data-source";
import type { ExternalSiteType } from "./site";

export interface IAnalyticsLocation {
  ip: string;
  postal: string;
  region: string;
  city: string;
  country: string;
}

export enum ORGANIZATION_STATUS {
  PENDING = -1, // 캠페인은 노출하지 않지만, pv는 수집
  INACTIVE = 0, // 캠페인 노출하지 않고 pv도 수집하지 않음. TODO: 현재 pv 수집은 무조건 수집하도록 되어있어서 수정 필요
  ACTIVE = 1,
}

export interface IAnalyticsBootFeature {
  sr?: {
    enabled?: boolean;
  };
}

/** Analytics Boot 응답으로부터 가져오는 부가적인 정보들을 정의합니다. */
export interface IAnalyticsBootResponse {
  /** 사용자의 위치 정보 */
  location: IAnalyticsLocation;

  /** 사이트의 통계 지표 목록 */
  goals: IGoal[];

  /** 고객의 팀이 캠페인을 노출할 수 있는 상태인지 체크 (플랜 제한 등) default: 1 */
  organizationStatus: ORGANIZATION_STATUS;
  /** 신규/재방문 고객 구분 */
  isNewUser: boolean;
  /** Listener 비활성화 여부 */
  disableListener: boolean;
  /** 추가 기능 */
  features: IAnalyticsBootFeature;
  /** UTM 파라미터 정보 */
  utm: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  };
}

export interface IGoal {
  id: number;
  icon?: string;
  goalType: string;
  goalName: string;
  goalTrigger: GoalCustomTrigger;
  eventName?: string;
  pagePath: string;
  siteId: string;
  currency: string;
}

export type GoalCustomTrigger =
  | IClickTrigger
  | IPageLoadTrigger
  | IWatchNetworkTrigger;

type TriggerType = GoalCustomTrigger["triggerType"];

type StringOperator = "EQUAL" | "INCLUDE" | "STARTS_WITH" | "ENDS_WITH";

interface IBaseGoalTrigger {
  triggerType: TriggerType;
  siteType: ExternalSiteType;
  condition: IPageloadCondition | INetworkCondition | IClickCondition[];
  // collects 배열의 값은 모두 수집
  collectTargets?: IHTMLCollect[];
}

interface IPageloadCondition {
  operator: StringOperator;
  value: string;
}

interface IClickCondition {
  type: "ID" | "CLASS";
  operator: StringOperator;
  value: string;
}

interface INetworkCondition {
  operator: StringOperator;
  value: string;
  method: "GET" | "POST";
  responseStatus: 200 | 301;
}

export interface IHTMLCollect extends IClickCondition {
  // "revenue.amount" => revenue: { amount: 000 }, "revenue.currency" => revenue: {currency: 'KRW'}, "props.foo" => props: { foo: bar }
  propKey: string;
  parseType?: "STRING" | "NUMBER";
}

interface IClickTrigger extends IBaseGoalTrigger {
  triggerType: "CLICK";
  // conditions배열의 값들은 AND 조건
  condition: IClickCondition;
}
interface IPageLoadTrigger extends IBaseGoalTrigger {
  triggerType: "PAGE_LOAD";
  condition: IPageloadCondition;
}

interface IWatchNetworkTrigger extends IBaseGoalTrigger {
  triggerType: "WATCH_NETWORK";
  condition: INetworkCondition;
}

export interface IAnalyticsManager {
  /**
   * first visit 이벤트 생성을 막기 위해 ignoreAnalyticsEvent 인자도 함께 넘깁니다.
   * TODO: first_visit 이벤트가 boot 시점에서 생성되지 않도록 변경되면 ignoreAnalyticsEvent 인자 제거
   */
  sendBoot(args: {
    siteId: string;
    ignoreAnalyticsEvent: boolean;
  }): Promise<IAnalyticsBootResponse>;

  sendSetUser(args: { user: { [key: string]: unknown } }): Promise<void>;

  sendResetUser(): Promise<void>;

  checkSessionIdChange(siteId: string): Promise<void>;

  /**
   * Analytics 이벤트를 보냅니다. 단, Validation 처리에 따라 이벤트가 송신되지 않을 수 있습니다.
   *
   * - ignore이 true인 경우, 이벤트가 송신되지 않습니다.
   * - 현재 위치가 "최상위 window"가 아니라면 pageview 이벤트가 송신되지 않습니다.
   * - eventKey를 만들어 적절히 넘겨줍니다.
   */
  sendAnalyticsEvent(event: AnalyticsEvent): Promise<void>;

  setIgnoreAnalyticsEvent(ignore: boolean): void;

  setIsEntry(isEntry: boolean): void;

  getDeviceId(): string;

  getSessionId(): string;

  getCommonFormData(args: {
    siteId: string;
    diagnosticData?: unknown;
    eventKey?: string;
  }): Record<string, string>;
}

export type DataMapItem = {
  alias: string;
  campaignPublicId: string;
  id: string;
  json: PartialData["data"];
};

export type DataJsonMapState = {
  addDataJson: (args: DataMapItem) => void;
  map: Record<string, IDataMap>;
};

export type NodeEventData = {
  documentNode: PartialNode<IDocumentNode>;
  nodeId: string;
  nodeName?: string;
  trigger: Trigger;
  action: Action;
};

export type ReactionEventData = {
  nodeId: string;
  nodeName?: string;
  trigger: Trigger;
  action: Action;
};

export type AnalyticsCampaignType =
  | "POPUP"
  | "INSTAGRAM"
  | "BANNER_GROUP"
  | "BANNER"
  | "POPUP_GROUP"
  | "FORM_EVENT"
  | "FORM_SURVEY"
  | "PROMOTION"
  | "CHILD_POPUP"
  | "COLLECTION"
  | "CHILD_EVENT_PAGE";

export interface IBaseAnalyticsEvent {
  type: string;
  siteId: string;
  diagnosticData?: unknown;
  eventKey?: string;
  meta?: { [key: string]: unknown };
}

export interface ICampaignRelatedEvent {
  campaignId: string;
  campaignType: AnalyticsCampaignType;
  parentCampaignId?: string;
}

export interface ISessionStartEvent extends IBaseAnalyticsEvent {
  type: "SESSION_START";
}

export interface IPageviewEvent extends IBaseAnalyticsEvent {
  type: "PAGEVIEW";
}

export interface IEngagementEvent extends IBaseAnalyticsEvent {
  type: "ENGAGEMENT";
  commonFormData: Record<string, string>;
  scrollDepth: number;
  engagementTime: number;
}

export interface IImpressionEvent
  extends IBaseAnalyticsEvent,
    ICampaignRelatedEvent {
  type: "IMPRESSION";
}

export interface IReactionEvent
  extends IBaseAnalyticsEvent,
    ICampaignRelatedEvent {
  type: "REACTION";
  data: ReactionEventData;
}

export interface IUserDefinedEvent extends IBaseAnalyticsEvent {
  type: "USER_DEFINED_EVENT";
  name: string;
  loginId?: string;
  revenue?: {
    amount: number;
    currency: string;
  };
  discount?: {
    amount: number;
  };
  shippingFee?: {
    amount: number;
  };
  mileage?: {
    amount: number;
  };
}

export interface ILeadEvent extends IBaseAnalyticsEvent, ICampaignRelatedEvent {
  type: "LEAD";
}

export type AnalyticsEvent =
  | ISessionStartEvent
  | IPageviewEvent
  | IImpressionEvent
  | IEngagementEvent
  | IReactionEvent
  | IUserDefinedEvent
  | ILeadEvent;

export interface IProductPageInfo {
  type: "PRODUCT";
  id: string;
  name?: string; // 상품명
  image?: string; //상품 이미지
  price?: string; // 상품 원가격
  discount?: string; // 상품최종가격
  currency?: string; // 통화 ex) KRW
  variant1?: string; // 옵션1코드(색상) CAFE24는 색상 + 사이즈의 복합코드로 존재
  variant2?: string; // 옵션1코드(사이즈)
  categoryId?: string; // 카테고리 아이디
  categoryName?: string; // 카테고리명
  [key: string]: unknown;
}

export interface ICategoryPageInfo {
  type: "CATEGORY";
  ids: string[];
  [key: string]: unknown;
}

export interface ICartPageInfo {
  type: "CART";
  products: {
    id: string;
    count: number;
    amount: number;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface IOrderSuccessPageInfo {
  type: "ORDER_SUCCESS";
  id: string;
  [key: string]: unknown;
}

export interface IUnknownPageInfo {
  type: "UNKNOWN";
  [key: string]: unknown;
}

export type PageInfo =
  | IProductPageInfo
  | ICategoryPageInfo
  | ICartPageInfo
  | IOrderSuccessPageInfo
  | IUnknownPageInfo;

export interface IOrderInfoRes {
  id: string;
  amount: number;
  currency?: string;
  count: number;
  items: {
    id: string;
    option?: {
      id: string;
    };
    name: string;
    count: number;
    amount: number;
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export interface ICartItem {
  id: string;
  name: string;
  price: number;
  count: number;
  amount: number;
  [key: string]: unknown;
}

export type GetCartInfoRes = {
  items?: ICartItem[];
  amount: number;
  totalCount: number;
} | null;

export type GetWishlistInfoRes = {
  count: number;
} | null;

export type GetCouponInfoRes = {
  count: number;
  list?: unknown[];
  [key: string]: unknown;
} | null;

/** 로그인 되어 있는 사용자 정보 */
export interface ILoggedInUser {
  type: "LOGGED_IN";
  id?: string;
  name?: string;
  email?: string;
  [key: string]: unknown;
}

/** 로그인은 되어있지만 사용자 정보를 알 수 없는 경우 */
export interface ILoggedInUnknownUser {
  type: "LOGGED_IN_UNKNOWN";
}

export type GetLoginUserRes = ILoggedInUser | ILoggedInUnknownUser | null;
